import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Crown, CalendarClock, Ticket, CreditCard, ArrowRight, Receipt, ChevronDown, RefreshCw, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { getPricing, formatPrice } from "@/lib/pricing";
import { formatDateID } from "@/lib/currency";

const DAY = 86400000;

const SOURCE_LABEL = {
  voucher: { label: "Voucher", Icon: Ticket },
  subscription: { label: "Pembayaran", Icon: CreditCard },
  trial: { label: "Masa coba gratis", Icon: Crown },
};

function PaymentHistory({ dark }) {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .subscriptionHistory()
      .then((r) => { if (!cancelled) setItems(r.items || []); })
      .catch(() => { if (!cancelled) setItems([]); });
    return () => { cancelled = true; };
  }, []);

  if (!items || items.length === 0) return null;

  const border = dark ? "border-white/10" : "border-zinc-100";
  const head = dark ? "text-zinc-300" : "text-zinc-700";
  const sub = dark ? "text-zinc-500" : "text-zinc-400";
  const amt = dark ? "text-white" : "text-zinc-900";

  return (
    <div className={`mt-4 border-t ${border} pt-3`} data-testid="payment-history">
      <button
        data-testid="payment-history-toggle"
        onClick={() => setOpen((o) => !o)}
        className={`flex w-full items-center justify-between text-xs font-semibold ${head}`}
      >
        <span className="flex items-center gap-1.5">
          <Receipt className="h-3.5 w-3.5" /> Riwayat Pembayaran ({items.length})
        </span>
        <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <ul className="mt-3 space-y-2.5">
          {items.map((it, i) => (
            <li key={i} className="flex items-center justify-between gap-3" data-testid={`payment-history-item-${i}`}>
              <div className="min-w-0">
                <p className={`truncate text-sm font-medium ${amt}`}>
                  {it.method} · {it.days} hari
                </p>
                <p className={`text-[11px] ${sub}`}>
                  {it.date ? formatDateID(it.date) : "—"}
                </p>
              </div>
              <span className={`shrink-0 font-mono text-sm font-bold ${it.amount > 0 ? amt : "text-emerald-500"}`}>
                {it.amount > 0 ? formatPrice(it.amount) : "Gratis"}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function RecurringStatus() {
  const [rec, setRec] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () =>
    api.subscriptionRecurring().then(setRec).catch(() => setRec({ active: false }));
  useEffect(() => {
    let cancelled = false;
    api.subscriptionRecurring().then((r) => { if (!cancelled) setRec(r); }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  if (!rec || !rec.active) return null;

  const cancel = async () => {
    setBusy(true);
    try {
      await api.subscriptionRecurringCancel();
      toast.success("Perpanjang otomatis dibatalkan. Premium tetap aktif hingga masa berlaku.");
      await load();
    } catch (e) {
      toast.error(e.message || "Gagal membatalkan.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-3 flex items-center justify-between gap-3 rounded-2xl bg-white/5 px-3 py-2.5" data-testid="recurring-status">
      <span className="flex items-center gap-2 text-xs font-semibold text-emerald-300">
        <RefreshCw className="h-3.5 w-3.5" /> Perpanjang otomatis aktif
      </span>
      <button
        data-testid="recurring-cancel-btn"
        onClick={cancel}
        disabled={busy}
        className="text-xs font-semibold text-zinc-400 underline transition-colors hover:text-white"
      >
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Batalkan"}
      </button>
    </div>
  );
}

export const SubscriptionCard = () => {
  const navigate = useNavigate();
  const { premium, subscription, subscriptionUntil, now } = useApp();
  const { isAuthenticated } = useAuth();
  const pricing = getPricing();

  const daysLeft =
    premium && subscriptionUntil
      ? Math.max(0, Math.ceil((subscriptionUntil - now) / DAY))
      : 0;
  const src = SOURCE_LABEL[subscription?.source] || null;
  const isTrial = subscription?.source === "trial";

  if (premium && subscriptionUntil) {
    const soon = daysLeft <= 3;
    return (
      <div
        className="mb-4 overflow-hidden rounded-3xl bg-zinc-900 p-5 text-white shield-shadow"
        data-testid="my-subscription-card"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-emerald-400">
            <Crown className="h-5 w-5" />
            <span className="text-xs font-semibold uppercase tracking-wider">
              Langganan Saya
            </span>
          </div>
          <span
            className={`rounded-full px-3 py-1 text-[11px] font-bold ${isTrial ? "bg-amber-500/20 text-amber-300" : "bg-emerald-500/20 text-emerald-300"}`}
            data-testid="subscription-status-badge"
          >
            {isTrial ? "MASA COBA" : "PREMIUM AKTIF"}
          </span>
        </div>

        <div className="mt-4 flex items-start gap-3">
          <CalendarClock className="mt-0.5 h-5 w-5 shrink-0 text-zinc-400" />
          <div>
            <p className="text-sm font-semibold text-white" data-testid="subscription-until">
              {isTrial ? "Masa coba hingga" : "Aktif hingga"} {formatDateID(subscriptionUntil)}
            </p>
            <p className={`text-xs ${soon ? "text-amber-300" : "text-zinc-400"}`} data-testid="subscription-days-left">
              {daysLeft === 0
                ? "Berakhir hari ini"
                : daysLeft === 1
                ? "Berakhir besok"
                : `Tersisa ${daysLeft} hari`}
            </p>
          </div>
        </div>

        {src && (
          <div className="mt-3 flex items-center gap-2 text-xs text-zinc-400" data-testid="subscription-source">
            <src.Icon className="h-3.5 w-3.5" />
            {isTrial ? src.label : `Diaktifkan via ${src.label}`}
          </div>
        )}

        <Button
          data-testid="subscription-renew-btn"
          onClick={() => navigate("/paywall")}
          className="mt-4 h-12 w-full rounded-full bg-emerald-600 text-sm font-semibold hover:bg-emerald-700"
        >
          {isTrial ? "Langganan Sekarang" : "Perpanjang Langganan"} — {formatPrice(pricing.price)}/bulan
          <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
        {soon && (
          <p className="mt-2 text-center text-[11px] text-amber-300">
            {isTrial
              ? "Berlangganan sekarang agar akses tidak terputus saat masa coba berakhir."
              : "Perpanjang sekarang agar akses premium tidak terputus."}
          </p>
        )}

        {!isTrial && <RecurringStatus />}
        <PaymentHistory dark />
      </div>
    );
  }

  return (
    <div
      className="mb-4 rounded-3xl border border-zinc-200 bg-white p-5 shield-shadow"
      data-testid="my-subscription-card"
    >
      <div className="flex items-center gap-2">
        <Crown className="h-5 w-5 text-zinc-400" />
        <p className="font-heading text-sm font-bold text-zinc-900">Langganan Saya</p>
      </div>
      <p className="mt-2 text-xs text-zinc-500" data-testid="subscription-inactive-note">
        Anda belum berlangganan premium. Buka semua fitur ketahanan & analisis
        lengkap dengan berlangganan {formatPrice(pricing.price)}/bulan.
      </p>
      <Button
        data-testid="subscription-subscribe-btn"
        onClick={() => navigate("/paywall")}
        className="mt-4 h-12 w-full rounded-full bg-zinc-900 text-sm font-semibold hover:bg-zinc-800"
      >
        {isAuthenticated ? "Langganan Sekarang" : "Masuk & Langganan"}
        <ArrowRight className="ml-2 h-4 w-4" />
      </Button>

      {isAuthenticated && <PaymentHistory />}
    </div>
  );
};
